// client/src/components/ProtectedRoute/ProtectedRoute.jsx
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/components/hooks/useAuth';
import { getLogger } from "@/utils/logger";

const log = getLogger("ProtectedRoute");
const LOADING_TIMEOUT_MS = 15000;

const evaluateAccess = ({ requiredPermissions, grantedPermissions }) => {
  if (requiredPermissions.length === 0) {
    return true;
  }
  return grantedPermissions.some((perm) => requiredPermissions.includes(perm));
};


/**
 * Protected route component that checks for authentication and authorization
 * 
 * @param {Object} props - Component props
 * @param {ReactNode} props.children - The protected content
 * @param {string[]} [props.requiredPermissions=[]] - Permissions required to access this route
 * @returns {ReactNode} Protected content or redirect
 */
const ProtectedRoute = ({ 
  children, 
  requiredPermissions = [],
  unauthenticatedAction = 'home',
}) => {
  const { 
    isAuthenticated, 
    isLoading, 
    userPermissions, 
    identityUser,
    loginWithRedirect,
    authResolved,
    authReadyForProtectedRoutes,
    authLifecycleState,
  } = useAuth();
  const location = useLocation();
  const [loadingTimedOut, setLoadingTimedOut] = useState(false);
  const [authRedirectRequested, setAuthRedirectRequested] = useState(false);
  const resolvedPermissions = Array.isArray(userPermissions) ? userPermissions : [];
  const routeAuthReady = typeof authReadyForProtectedRoutes === "boolean"
    ? authReadyForProtectedRoutes
    : !isLoading;
  const authCheckPending = isLoading || (isAuthenticated && !routeAuthReady);

  // Create a detailed loading message for debugging
  const getLoadingMessage = () => {
    if (!identityUser) return "Waiting for account information...";
    return "Verifying access...";
  };

  useEffect(() => {
    if (!authCheckPending) {
      setLoadingTimedOut(false);
      return;
    }

    const timeoutId = setTimeout(() => {
      setLoadingTimedOut(true);
    }, LOADING_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [authCheckPending]);

  // Debug logging
  useEffect(() => {
    log.info(
      `[ProtectedRoute:useEffect] > [Request]: evaluate access > [Comment]: ${JSON.stringify({
        path: location.pathname,
        isAuthenticated,
        isLoading,
        authResolved,
        authReadyForProtectedRoutes: routeAuthReady,
        authLifecycleState,
        loadingTimedOut,
        grantedPermissions: resolvedPermissions,
        requiredPermissions,
        identity: identityUser ? `${identityUser.name} (${identityUser.email})` : "No identity",
      })}`
    );

    if (!authCheckPending) {
      // Check permissions first
      if (requiredPermissions.length > 0) {
        const hasPermission = resolvedPermissions.some(perm => requiredPermissions.includes(perm));
        log.info(
          `[ProtectedRoute:permissionCheck] > [Response]: ${hasPermission ? "GRANTED" : "DENIED"} > [Comment]: grantedPermissions=[${resolvedPermissions.join(
            ", "
          )}] requiredPermissions=[${requiredPermissions.join(", ")}]`
        );
      }
    }
  }, [
    authCheckPending,
    authLifecycleState,
    authResolved,
    isAuthenticated,
    isLoading,
    loadingTimedOut,
    location.pathname,
    requiredPermissions,
    resolvedPermissions,
    routeAuthReady,
    identityUser,
  ]);

  useEffect(() => {
    if (!loadingTimedOut) return;
    log.error(
      `[ProtectedRoute:loadingTimeout] > [Response]: timeout > [Comment]: path=${location.pathname} timeoutMs=${LOADING_TIMEOUT_MS}`
    );
  }, [loadingTimedOut, location.pathname]);

  useEffect(() => {
    if (isAuthenticated || unauthenticatedAction !== 'login' || authCheckPending || authRedirectRequested) {
      return;
    }

    setAuthRedirectRequested(true);
    loginWithRedirect({
      appState: {
        returnTo: `${location.pathname}${location.search}${location.hash}`,
      },
    });
  }, [
    authCheckPending,
    authRedirectRequested,
    isAuthenticated,
    location.hash,
    location.pathname,
    location.search,
    loginWithRedirect,
    unauthenticatedAction,
  ]);

  // Show loading while checking authentication
  if (authCheckPending && !loadingTimedOut) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#3f4f24] mb-4"></div>
        <p className="text-gray-600">{getLoadingMessage()}</p>
      </div>
    );
  }

  if (authCheckPending && loadingTimedOut) {
    return (
      <div className="flex flex-col items-center justify-center h-screen px-4 text-center">
        <p className="text-gray-700 font-semibold mb-2">Authentication check timed out.</p>
        <p className="text-gray-600 mb-4">Please retry loading this page.</p>
        <button
          type="button"
          className="px-4 py-2 rounded bg-[#3f4f24] text-white hover:bg-[#324c48]"
          onClick={() => window.location.reload()}
          id="protected-route-btn-retry-load"
        >
          Retry
        </button>
      </div>
    );
  }
  
  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    if (unauthenticatedAction === 'login') {
      return (
        <div className="flex flex-col items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#3f4f24] mb-4"></div>
          <p className="text-gray-600">Redirecting to login...</p>
        </div>
      );
    }

    log.info(
      "[ProtectedRoute:redirectUnauthenticated] > [Response]: redirect home"
    );
    return <Navigate to="/" state={{ from: location }} replace />;
  }
  
  const hasAccess = evaluateAccess({ requiredPermissions, grantedPermissions: resolvedPermissions });
  
  if (!hasAccess) {
    log.info(
      "[ProtectedRoute:redirectUnauthorized] > [Response]: redirect unauthorized"
    );
    // Redirect to unauthorized page
    return <Navigate to="/unauthorized" replace />;
  }
  
  // Identity is authenticated and authorized
  return children;
};

export default ProtectedRoute;
